export class TimeoutError extends Error {
  constructor(message = "Operation timed out") {
    super(message);
    this.name = "TimeoutError";
  }
}

/**
 * Settle with `promise`, but reject with a {@link TimeoutError} if it does not
 * settle within `timeoutMs`. `onTimeout` runs just before the rejection so the
 * caller can clean up the work that stalled (e.g. terminate a hung web worker).
 *
 * Use this to guarantee an `await` always settles when the awaited work has no
 * timeout of its own — a single never-resolving sub-task can otherwise hang the
 * caller forever (SN/ENG-1424: the invoice worker stalled and blocked the
 * payment-link POST indefinitely).
 */
export function promiseWithTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  onTimeout?: () => void
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout?.();
      reject(new TimeoutError(`Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}
